/**
 * Every tractor of the span, not ready first: who it is, what it is now and
 * since when, its runs, and one two-lane cell per day. Nothing is written
 * here; the last column goes to Tractor State.
 */
import React from "react";
import { styled } from "styled-components";
import DayCell from "./DayCell";
import { LaneLegend } from "./lanes";
import { customerLabel, dayHeader, describeNow, sinceText } from "./labels";
import { FleetQuery, toSearch } from "./query";
import { todayIso } from "./time";
import { FleetResponse } from "./types";
import { tokens } from "../TractorState/tokens";

interface FleetTableProps {
  fleet: FleetResponse;
  query: FleetQuery;
}

const FleetTable: React.FC<FleetTableProps> = ({ fleet, query }) => {
  const days = fleet.vehicles[0]?.days.map((d) => d.local_day) ?? [];
  const today = todayIso();
  const dayLink = (localDay: string) =>
    `/metrics?${toSearch({ ...query, span: "day", to: localDay, from: undefined })}`;
  return (
    <Card>
      <Head>
        <Title>
          Tractors · not ready first · each cell is that day, 00:00 to 24:00
        </Title>
        <LaneLegend />
      </Head>
      <Scroll>
        <Table>
          <thead>
            <tr>
              <th>Tractor</th>
              <th>Now</th>
              <th>Runs</th>
              {days.map((d) => (
                <th key={d}>
                  {dayHeader(d)}
                  {d === today ? " · today" : ""}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {fleet.vehicles.map((v) => {
              const now = describeNow(v.now);
              const since = v.now ? sinceText(v.now.since) : null;
              return (
                <tr key={v.vehicle}>
                  <td>
                    <Name>{v.display_name}</Name>
                    {v.customer && (
                      <Customer>{customerLabel(v.customer)}</Customer>
                    )}
                  </td>
                  <td>
                    <NowRow>
                      <Dot $tone={now.tone} />
                      <NowText $tone={now.tone}>{now.text}</NowText>
                    </NowRow>
                    {since && v.now?.value !== "UNSET" && (
                      <Since>{since}</Since>
                    )}
                  </td>
                  <Runs>{v.runs}</Runs>
                  {v.days.map((d) => (
                    <DayTd key={d.local_day}>
                      <DayCell
                        day={d}
                        displayName={v.display_name}
                        to={dayLink(d.local_day)}
                      />
                    </DayTd>
                  ))}
                  <td>
                    <StateLink
                      href={`/tractor-state/${encodeURIComponent(v.vehicle)}`}
                    >
                      Tractor State ↗
                    </StateLink>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {fleet.vehicles.length === 0 && (
          <Empty>No tractors match these filters.</Empty>
        )}
      </Scroll>
    </Card>
  );
};

export default FleetTable;

const Card = styled.div`
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  padding: 6px 8px 4px;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px 16px;
  padding: 10px 10px 4px;
`;

const Title = styled.h2`
  margin: 0;
  font: 600 14px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const Scroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font: 13px ${tokens.fontSans};
  min-width: 720px;
  th {
    text-align: left;
    font: 500 11px ${tokens.fontMono};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${tokens.slate};
    padding: 8px;
    border-bottom: 1px solid ${tokens.line};
    white-space: nowrap;
  }
  td {
    padding: 9px 8px;
    border-bottom: 1px solid ${tokens.line};
    vertical-align: middle;
  }
  tr:hover td {
    background: #fbfbfb;
  }
`;

const Name = styled.div`
  font-weight: 600;
  color: ${tokens.navy};
  white-space: nowrap;
`;

const Customer = styled.div`
  font: 500 10px ${tokens.fontMono};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.slate};
`;

const NowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

const Dot = styled.span<{ $tone: "ok" | "bad" | "muted" }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${(p) =>
    p.$tone === "ok" ? "#8fd79f" : p.$tone === "bad" ? "#c6262b" : "#d6d9de"};
`;

const NowText = styled.span<{ $tone: "ok" | "bad" | "muted" }>`
  font: 600 12px ${tokens.fontSans};
  color: ${(p) =>
    p.$tone === "ok"
      ? tokens.readyText
      : p.$tone === "bad"
        ? tokens.groundedText
        : tokens.slate};
`;

const Since = styled.div`
  font: 11px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const Runs = styled.td`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const DayTd = styled.td`
  min-width: 72px;
  padding: 9px 5px !important;
`;

const StateLink = styled.a`
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.blue};
  text-decoration: none;
  white-space: nowrap;
`;

const Empty = styled.p`
  margin: 12px 10px;
  font: 13px ${tokens.fontSans};
  color: ${tokens.slate};
`;
